/**
 * Turns the account's raw credit balances into a single, uniform list of
 * "buckets" — one per credit source (the plan allowance, plus every purchased
 * add-on lot). Every credit surface (sidebar badge, usage settings, billing
 * settings) renders this same model so they can never drift apart again.
 *
 * The grand total counts only *spendable* credits; paused add-ons (packs held
 * while there's no active paid plan) are surfaced as dimmed buckets but left out
 * of the headline number so it never overstates what the user can actually use.
 */

/** A bucket turns amber once it expires within this many days. */
export const CREDIT_EXPIRY_WARNING_DAYS = 7;

/** Credits are "low" once the spendable total drops below this share of the plan allowance. */
export const LOW_CREDIT_RATIO = 0.15;

export type CreditBucketsInput = {
  /** Credits left from this cycle's plan allowance. */
  subscriptionBalance: number;
  /** The plan's allowance for the active billing period (12x on annual plans). */
  creditAllowance: number;
  /** Whether purchased add-on packs can currently be spent. */
  packSpendable: boolean;
  /** Unspent add-on lots, ordered by nearest expiration. */
  packExpirations: { credits: number; total: number; expiresAt: string }[];
  /** When the plan pool next resets to its allowance (the plan pool's "expiry"). */
  nextResetAt: string | null;
};

export type CreditBucket = {
  id: string;
  kind: "plan" | "pack";
  label: string;
  /** Credits still available in this bucket. */
  remaining: number;
  /** Credits this bucket started the period with. */
  total: number;
  /** Credits already consumed from this bucket (never negative). */
  used: number;
  /** ISO date this bucket expires / resets, or `null` when it doesn't. */
  expiresAt: string | null;
  /** Whether this bucket's credits can currently be spent. */
  spendable: boolean;
  /** Whole days until expiry, or `null` when there's no expiry date. */
  daysToExpiry: number | null;
  /** Spendable and expiring within the warning window. */
  isUrgent: boolean;
};

export type CreditBreakdown = {
  /** Buckets, spendable-and-soonest-expiring first, paused ones last. */
  buckets: CreditBucket[];
  /** Sum of `remaining` across spendable buckets — the headline total. */
  grandTotal: number;
  /** Credits left from the plan allowance only. */
  planRemaining: number;
  /** The plan allowance (meter denominator). */
  planAllowance: number;
  /** Plan credits consumed this period. */
  planUsed: number;
  /** Spendable add-on credits (excludes paused packs). */
  packRemaining: number;
  /** Whether any paused (unspendable) pack credits exist. */
  hasPausedPacks: boolean;
  /** True once the spendable total drops into the low band. */
  isLow: boolean;
  /** The soonest-expiring spendable bucket, if any. */
  nextToExpire: CreditBucket | null;
};

function daysUntil(iso: string | null, now: number): number | null {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;
  return Math.ceil((at - now) / (24 * 60 * 60 * 1000));
}

/**
 * Sorts spendable buckets by soonest expiry first (undated last), then all
 * paused buckets after them in the same expiry order.
 */
function compareBuckets(a: CreditBucket, b: CreditBucket): number {
  if (a.spendable !== b.spendable) return a.spendable ? -1 : 1;
  const aAt = a.expiresAt ? new Date(a.expiresAt).getTime() : Infinity;
  const bAt = b.expiresAt ? new Date(b.expiresAt).getTime() : Infinity;
  return aAt - bAt;
}

export function buildCreditBreakdown(
  input: CreditBucketsInput,
  now: number = Date.now(),
): CreditBreakdown {
  const planRemaining = Math.max(0, input.subscriptionBalance);
  const planAllowance = Math.max(planRemaining, input.creditAllowance);
  const planUsed = Math.max(0, planAllowance - planRemaining);

  const planDays = daysUntil(input.nextResetAt, now);
  const planBucket: CreditBucket = {
    id: "plan",
    kind: "plan",
    label: "Plan credits",
    remaining: planRemaining,
    total: planAllowance,
    used: planUsed,
    expiresAt: input.nextResetAt,
    spendable: true,
    daysToExpiry: planDays,
    isUrgent: planDays !== null && planDays <= CREDIT_EXPIRY_WARNING_DAYS,
  };

  const packBuckets: CreditBucket[] = input.packExpirations.map((lot, i) => {
    const remaining = Math.max(0, lot.credits);
    const total = Math.max(remaining, lot.total);
    const days = daysUntil(lot.expiresAt, now);
    return {
      id: `pack-${i}`,
      kind: "pack",
      label: "Add-on pack",
      remaining,
      total,
      used: Math.max(0, total - remaining),
      expiresAt: lot.expiresAt,
      spendable: input.packSpendable,
      daysToExpiry: days,
      isUrgent: input.packSpendable && days !== null && days <= CREDIT_EXPIRY_WARNING_DAYS,
    };
  });

  const buckets = [planBucket, ...packBuckets].sort(compareBuckets);

  const grandTotal = buckets.reduce((sum, b) => (b.spendable ? sum + b.remaining : sum), 0);
  const packRemaining = packBuckets.reduce((sum, b) => (b.spendable ? sum + b.remaining : sum), 0);
  const hasPausedPacks = packBuckets.some((b) => !b.spendable);
  const isLow = planAllowance > 0 && grandTotal <= planAllowance * LOW_CREDIT_RATIO;
  const nextToExpire = buckets.find((b) => b.spendable && b.expiresAt !== null) ?? null;

  return {
    buckets,
    grandTotal,
    planRemaining,
    planAllowance,
    planUsed,
    packRemaining,
    hasPausedPacks,
    isLow,
    nextToExpire,
  };
}
